// Fuso do escritório — ponto único de formatação de data/hora no front.
//
// Por que existe: `new Date(iso).toLocaleString('pt-BR')` formata no fuso do
// NAVEGADOR. O backend manda ISO em UTC, então um advogado com o notebook em
// outro fuso (viagem, máquina configurada errada, VPS de acesso remoto) vê a
// audiência em outra hora — e perde. O horário da agenda é hora de fórum, não
// hora de quem está olhando: tem que ser Brasília para todo mundo, sempre.
//
// O fuso é o do ESCRITÓRIO (Fase 5, 13/10/2026): Brasília por padrão, e um dos
// quatro fusos legais (Lei 12.876/2013) quando o escritório escolhe — o prazo
// vale no horário do juízo (CPC 213). O servidor diz qual (`account.fuso`,
// Advup::FusoDoEscritorio); `definirFusoDoEscritorio` é chamado quando a conta
// chega (store `accounts/get`), antes das telas.
//
// `spec/agendaFusoFixo.spec.js` falha se alguém formatar data na agenda sem
// passar por aqui — é o que impede a regressão voltar em tela nova.
export const FUSO_PADRAO = 'America/Sao_Paulo';
export const FUSOS_DO_BRASIL = [
  'America/Noronha',
  'America/Sao_Paulo',
  'America/Manaus',
  'America/Rio_Branco',
];

// `let` exportado de propósito: o import ES é uma ligação VIVA — quem importa
// `FUSO_ESCRITORIO` lê o valor de agora, já trocado pela conta. Ler o valor no
// topo de um módulo (`const X = FUSO_ESCRITORIO`) congelaria o padrão.
export let FUSO_ESCRITORIO = FUSO_PADRAO;

// Fora dos quatro legais (ou vazio): Brasília, nunca o fuso do navegador.
export const definirFusoDoEscritorio = fuso => {
  FUSO_ESCRITORIO = FUSOS_DO_BRASIL.includes(fuso) ? fuso : FUSO_PADRAO;
  return FUSO_ESCRITORIO;
};

// Locale só muda o IDIOMA (nomes de mês, ordem dd/mm). O fuso é sempre o do
// escritório, independente de idioma — por isso ele não é parametrizável.
const LOCALE_PADRAO = 'pt-BR';

// `new Date(null)` devolve a epoch (01/01/1970), não uma data inválida — sem
// esta guarda um campo vazio viraria "01/01/1970" na tela em vez de ficar em
// branco.
const paraData = valor => {
  if (valor === null || valor === undefined || valor === '') return null;
  return valor instanceof Date ? valor : new Date(valor);
};

const valido = data => data instanceof Date && !Number.isNaN(data.getTime());

/**
 * Mescla o fuso do escritório nas opções do Intl.
 * Use quando precisar de opções que os helpers abaixo não cobrem.
 */
export const comFusoEscritorio = (opcoes = {}) => ({
  ...opcoes,
  timeZone: FUSO_ESCRITORIO,
});

/**
 * Valor só-data, como o banco guarda em coluna `date`: "2026-02-23".
 */
const SO_DATA = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Data: 04/08/2026 (ou o formato que as opções pedirem).
 *
 * 🔴 TRATA OS DOIS FORMATOS QUE O BANCO DEVOLVE, e isso é o ponto:
 *
 * - "2026-02-23" (coluna `date`: vencimento, ajuizamento) NÃO é um instante,
 *   é o mesmo dia em qualquer lugar do mundo. `new Date("2026-02-23")` cria
 *   meia-noite UTC, e renderizar isso no fuso de São Paulo mostra o DIA
 *   ANTERIOR. Era exatamente por isso que várias telas tinham o seu próprio
 *   formatador feito à mão, montando a data pelos pedaços — e foi de um
 *   desses que saiu o "23T00:00:00.000/02/2026" que apareceu na tela em
 *   10/09/2026: ele partia por "-" e o "dia" vinha com a hora colada.
 *   Aqui o valor só-data é formatado pelos pedaços e SEM conversão de fuso:
 *   o dia sai igual para quem olha de Vitória ou de Tóquio.
 *
 * - "2026-02-23T14:30:00.000Z" (coluna `datetime`: pagamento, criação) É um
 *   instante, e aí o fuso do escritório vale: prazo é hora de fórum.
 */
export const dataEscritorio = (valor, opcoes = {}, locale = LOCALE_PADRAO) => {
  const pedacos = typeof valor === 'string' && valor.match(SO_DATA);
  if (pedacos) {
    const [, ano, mes, dia] = pedacos;
    return new Date(
      Number(ano),
      Number(mes) - 1,
      Number(dia)
    ).toLocaleDateString(locale, opcoes);
  }

  const data = paraData(valor);
  if (!valido(data)) return '';
  return data.toLocaleDateString(locale, comFusoEscritorio(opcoes));
};

/** Hora: 14:30 por padrão. */
export const horaEscritorio = (
  valor,
  opcoes = { hour: '2-digit', minute: '2-digit' },
  locale = LOCALE_PADRAO
) => {
  const data = paraData(valor);
  if (!valido(data)) return '';
  return data.toLocaleTimeString(locale, comFusoEscritorio(opcoes));
};

/** Data e hora juntas: 04/08/2026 14:30. */
export const dataHoraEscritorio = (
  valor,
  opcoes = {},
  locale = LOCALE_PADRAO
) => {
  const data = paraData(valor);
  if (!valido(data)) return '';
  return data.toLocaleString(locale, comFusoEscritorio(opcoes));
};

/**
 * O dia (`YYYY-MM-DD`) em que um INSTANTE cai em Brasília: "agora", "30 dias
 * atrás", um `paid_at` que veio do servidor. É o que substitui
 * `toISOString().slice(0, 10)` — que dá o dia em UTC e, depois das 21h, já é
 * amanhã (Victor, 29/09/2026: "Hoje" das Publicações vazio à noite).
 */
export const diaEscritorio = valor => {
  const data = paraData(valor);
  if (!valido(data)) return '';
  const [dia, mes, ano] = dataEscritorio(data).split('/');
  return `${ano}-${mes}-${dia}`;
};

/**
 * "Hoje" em Brasília, como `YYYY-MM-DD`.
 *
 * `new Date().toISOString().slice(0, 10)` devolve o dia em UTC — entre 21h e
 * meia-noite isso já é amanhã, e a agenda marcaria o dia errado como hoje.
 * Mesma classe de bug que o backend tinha com `Date.today`.
 */
export const hojeEscritorio = (agora = new Date()) => diaEscritorio(agora);

/**
 * Um dia de CALENDÁRIO já montado (a grade da agenda, o 1º do mês do
 * Financeiro — `new Date(ano, mes, dia)`) como `YYYY-MM-DD`, pelas partes
 * dele. Converter de fuso aqui mudaria o dia: não é instante, é data.
 */
export const diaDoCalendario = d =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * O RÓTULO de um dia de CALENDÁRIO já montado (`new Date(ano, mes, dia)`):
 * "outubro de 2026", "2 de outubro". Formata pelas PARTES da data, em UTC,
 * para nenhum fuso mudar o dia — a mesma razão de `diaDoCalendario`. Nasceu
 * para o minicalendário da Agenda (02/10/2026), que formatava no fuso do
 * navegador.
 */
export const rotuloDoDiaDoCalendario = (d, opcoes = {}, locale = 'pt-BR') =>
  new Date(
    Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
  ).toLocaleDateString(locale, { ...opcoes, timeZone: 'UTC' });

const DIA_EM_MS = 86400000;
const diaEmUtc = iso => {
  const [ano, mes, dia] = iso.split('-').map(Number);
  return Date.UTC(ano, mes - 1, dia);
};

/**
 * Quantos dias do calendário do ESCRITÓRIO vão de hoje até `valor` (negativo
 * = já passou). Às 22h30 de Brasília o relógio UTC já está no dia seguinte:
 * contar pelo navegador dizia "hoje" para o compromisso de amanhã.
 */
export const diasAteEscritorio = (valor, agora = new Date()) =>
  Math.round(
    (diaEmUtc(hojeEscritorio(new Date(valor))) -
      diaEmUtc(hojeEscritorio(agora))) /
      DIA_EM_MS
  );
